// src/pages/MatrixPage.jsx - WITH ADD TO PROSPECT LIST INTEGRATION
// CLIENT ID: Uses AuthContext session.client_id as primary source
import React, { useState, useEffect, useMemo, useLayoutEffect, useRef, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { buildApiUrl } from '@/config/appConfig';
import { calibrateUseCases } from '../utils/calibration';
import { sortUseCases } from '../utils/ranking';
import { LegendPopover } from '../components/LegendPopover';
import UseCasePreview from '../components/UseCasePreview';
import FiltersToolbar from '../components/FiltersToolbar';
import { HelpCircle, RefreshCw, AlertCircle } from 'lucide-react';
import MatrixSVGChart from '../components/MatrixSVGChart';

export default function MatrixPage({ onUseCaseClick }) {
  console.log('[MatrixPage] Component rendering');
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // ============================================================
  // CLIENT ID: Get from AuthContext session (single source of truth)
  // localStorage override is for admin/dev client switching only
  // ============================================================
  const { session, sessionLoading } = useAuth();
  const sessionClientId = session?.client_id || null;

  // ---- state ----
  const [useCases, setUseCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUseCase, setSelectedUseCase] = useState(null);
  const [highlightedUseCase, setHighlightedUseCase] = useState(null);
  
  // Client override from localStorage (for admin/dev switching)
  const [clientOverride, setClientOverride] = useState(() => {
    return localStorage.getItem('dva_current_client') || null;
  });
  
  // Effective client: override takes precedence over session
  const currentClientId = clientOverride || sessionClientId;
  
  // Status tracking state
  const [statuses, setStatuses] = useState([]);
  const [statusesLoading, setStatusesLoading] = useState(false);
  
  // Keyboard navigation state
  const [keyboardHighlightIndex, setKeyboardHighlightIndex] = useState(-1);
  const listContainerRef = useRef(null);
  const listItemRefs = useRef({});
  
  // Single source of truth for active use case
  const activeUseCase = selectedUseCase || highlightedUseCase;

  // URL-driven filter state
  const rankMode = searchParams.get('rank') || 'balanced';
  const [displayMode, setDisplayMode] = useState(
    searchParams.get('space') === 'cal' ? 'Calibrated' :
    searchParams.get('space') === 'raw' ? 'Raw' : 'Auto'
  );

  // Category mode: 'core' or 'client' (from URL param 'mode')
  const categoryMode = searchParams.get('mode') || 'core';
  
  // Selected category (from URL: 'cat' for core, 'ccat' for client)
  const selectedCategory = categoryMode === 'client' 
    ? (searchParams.get('ccat') || 'all')
    : (searchParams.get('cat') || 'all');
  
  const selectedType = searchParams.get('type') || 'all';
  const searchQuery = searchParams.get('q') || '';
  const sortBy = searchParams.get('sort') || 'total';

  // ---- runtime sizing refs ----
  const filterRef = useRef(null);
  const matrixCardRef = useRef(null);
  const [squareSize, setSquareSize] = useState(560);

  // Measure available viewport space and size the matrix to fit without scroll
  useLayoutEffect(() => {
    function recompute() {
      if (!filterRef.current || !matrixCardRef.current) return;

      const filterBottom = filterRef.current.getBoundingClientRect().bottom;
      const innerGaps = 12 + 8;
      const pagePadding = 12;
      const availableH = Math.max(280, window.innerHeight - filterBottom - innerGaps - pagePadding);

      matrixCardRef.current.style.height = `${availableH}px`;

      const cardWidth = matrixCardRef.current.clientWidth - 8;
      const size = Math.max(260, Math.min(cardWidth, availableH - 8));
      setSquareSize(size);
    }

    recompute();

    const ro = new ResizeObserver(recompute);
    ro.observe(document.body);
    if (matrixCardRef.current) ro.observe(matrixCardRef.current);
    window.addEventListener('resize', recompute);

    return () => {
      ro.disconnect();
      window.removeEventListener('resize', recompute);
    };
  }, []);

  // Listen for client changes (from admin/dev toggle)
  useEffect(() => {
    const handleClientChange = (event) => {
      const newClientId = event.detail.clientId;
      console.log('[MatrixPage] Client override changed to:', newClientId);
      setClientOverride(newClientId);
    };

    window.addEventListener('clientChanged', handleClientChange);
    return () => window.removeEventListener('clientChanged', handleClientChange);
  }, []);

  // ---- Loading state - wait for session ----
  if (sessionLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '400px',
        backgroundColor: '#f8fafc'
      }}>
        <RefreshCw className="w-8 h-8 animate-spin mb-4" style={{ color: '#18365E' }} />
        <p style={{ color: '#64748b', fontSize: 14 }}>Loading your session...</p>
      </div>
    );
  }

  // ---- No client ID state ----
  if (!currentClientId) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '400px',
        backgroundColor: '#f8fafc',
        padding: 24
      }}>
        <AlertCircle className="w-12 h-12 mb-4" style={{ color: '#f59e0b' }} />
        <h2 style={{ fontSize: 18, fontWeight: 600, color: '#18365E', marginBottom: 8 }}>
          No Organization Selected
        </h2>
        <p style={{ color: '#64748b', fontSize: 14, textAlign: 'center', maxWidth: 400, marginBottom: 16 }}>
          Your account is not currently associated with an organization. 
          Please contact your administrator or complete onboarding to view the portfolio matrix.
        </p>
        <button
          onClick={() => navigate('/home')}
          style={{
            padding: '8px 16px',
            backgroundColor: '#18365E',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            fontSize: 14,
            cursor: 'pointer'
          }}
        >
          Return to Home
        </button>
      </div>
    );
  }

  // ---- data fetch ----
  useEffect(() => {
    const fetchUseCases = async () => {
      if (!currentClientId) return;
      
      try {
        setLoading(true);
        setError(null);
        
        console.log('[MatrixPage] Fetching use cases for client:', currentClientId);

        const url = buildApiUrl(`/api/v2/useCases/list?clientId=${currentClientId}`);
        const response = await fetch(url);

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          throw new Error('API endpoint not available - received HTML instead of JSON');
        }
        if (!response.ok) throw new Error(`HTTP ${response.status}: Failed to fetch use cases`);

        const data = await response.json();
        let cases = data.useCases || data.use_cases || data.data || data || [];

        // Filter out unranked use cases (rank === null or scores === 0)
        cases = cases.filter(uc =>
          uc.rank !== null &&
          uc.rank !== undefined &&
          (uc.value_score > 0 || uc.implementation_score > 0)
        );

        console.log(`[MatrixPage] Received ${cases.length} use cases`);
        setUseCases(Array.isArray(cases) ? cases : []);
        
        if (data.totalCount) {
          window.__clientUseCaseTotal = data.totalCount;
        }
      } catch (err) {
        console.error('[MatrixPage] API Error:', err);
        setError(err.message);
        setUseCases([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchUseCases();
  }, [currentClientId]);

  // Fetch statuses to track which use cases are prospects
  useEffect(() => {
    const fetchStatuses = async () => {
      if (!currentClientId) return;
      
      setStatusesLoading(true);
      try {
        console.log(`[MatrixPage] Fetching statuses for client ${currentClientId}...`);
        const response = await fetch(
          buildApiUrl(`/api/v2/usecases/${currentClientId}/statuses`)
        );
        
        if (response.ok) {
          const data = await response.json();
          console.log(`[MatrixPage] Fetched ${data.count} statuses`);
          setStatuses(data.statuses || []);
        }
      } catch (error) {
        console.error('[MatrixPage] Error fetching statuses:', error);
        setStatuses([]);
      } finally {
        setStatusesLoading(false);
      }
    };
    
    fetchStatuses();
  }, [currentClientId]);

  // Check if use case is in prospect list
  const isInProspectList = useCallback((useCaseId) => {
    return statuses.some(s => s.use_case_id === useCaseId && s.status === 'prospect');
  }, [statuses]);

  // Refetch statuses after adding to prospect list
  const refetchStatuses = async () => {
    if (!currentClientId) return;

    try {
      const response = await fetch(
        buildApiUrl(`/api/v2/usecases/${currentClientId}/statuses`)
      );
      
      if (response.ok) {
        const data = await response.json();
        setStatuses(data.statuses || []);
      }
    } catch (error) {
      console.error('[MatrixPage] Error refetching statuses:', error);
    }
  };

  // ---- calibration & ranking ----
  const { calibratedUseCases, calMap, bounds } = useMemo(() => {
    if (!useCases.length) return { calibratedUseCases: [], calMap: {}, bounds: null };
    const result = calibrateUseCases(useCases);
    return { calibratedUseCases: result.useCases, calMap: result.calMap, bounds: result.bounds };
  }, [useCases]);

  const rankContext = useMemo(() => ({
    mode: rankMode,
    typeBlend: 0.5,
    displayMode,
    valueBounds: bounds?.value || { min: 0, max: 100, calMin: 35, calMax: 80 },
    eoiBounds: bounds?.eoi || { min: 0, max: 100, calMin: 35, calMax: 80 }
  }), [rankMode, displayMode, bounds]);

  const sortedUseCases = useMemo(() => {
    if (!calibratedUseCases.length) return [];
    return sortUseCases(calibratedUseCases, rankContext, calMap);
  }, [calibratedUseCases, rankContext, calMap]);

  // ---- Extract categories ----
  const uniqueCoreCategories = useMemo(() => {
    const cats = new Set();
    useCases.forEach(uc => {
      const category = uc.category_name || uc.category;
      if (category && typeof category === 'string') {
        cats.add(category.trim());
      }
    });
    return ['all', ...Array.from(cats).sort()];
  }, [useCases]);

  const uniqueClientCategories = useMemo(() => {
    const clientCats = new Set();
    useCases.forEach(uc => {
      if (uc.client_category_names && Array.isArray(uc.client_category_names)) {
        uc.client_category_names.forEach(cat => {
          if (cat && typeof cat === 'string') {
            clientCats.add(cat.trim());
          }
        });
      }
    });
    return ['all', ...Array.from(clientCats).sort()];
  }, [useCases]);

  const uniqueTypes = useMemo(() => {
    const types = new Set();
    useCases.forEach(uc => {
      const type = uc.type || uc.priority_label;
      if (type) types.add(type);
    });
    return ['all', ...Array.from(types).sort()];
  }, [useCases]);

  // Reset category selection if switching modes and current selection is invalid
  useEffect(() => {
    const currentList = categoryMode === 'client' ? uniqueClientCategories : uniqueCoreCategories;
    if (!currentList.includes(selectedCategory)) {
      const newParams = new URLSearchParams(searchParams);
      if (categoryMode === 'client') {
        newParams.set('ccat', 'all');
        newParams.delete('cat');
      } else {
        newParams.set('cat', 'all');
        newParams.delete('ccat');
      }
      setSearchParams(newParams, { replace: true });
    }
  }, [categoryMode, uniqueClientCategories, uniqueCoreCategories, selectedCategory, searchParams, setSearchParams]);

  // ---- Filter use cases based on mode ----
  const filteredUseCases = sortedUseCases.filter(uc => {
    const typeMatch = selectedType === 'all' || 
      uc.type === selectedType || 
      uc.priority_label === selectedType;
    
    let categoryMatch = true;
    if (categoryMode === 'client') {
      if (selectedCategory !== 'all') {
        categoryMatch = uc.client_category_names && 
          Array.isArray(uc.client_category_names) &&
          uc.client_category_names.includes(selectedCategory);
      }
    } else {
      categoryMatch = selectedCategory === 'all' || 
        uc.category === selectedCategory || 
        uc.category_name === selectedCategory;
    }
    
    const searchMatch = !searchQuery || 
      (uc.title || uc.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (uc.category || uc.category_name || '').toLowerCase().includes(searchQuery.toLowerCase());
    
    return typeMatch && categoryMatch && searchMatch;
  });

  // ---- Grouping key for legend/colors ----
  const getGroupKey = useCallback((uc) => {
    if (categoryMode === 'client') {
      return (uc.client_category_names && uc.client_category_names[0]) || 'Unassigned';
    } else {
      return uc.category_name || uc.category || 'Uncategorized';
    }
  }, [categoryMode]);

  // Build legend from unique group keys in filtered data
  const legendItems = useMemo(() => {
    const groups = new Set();
    filteredUseCases.forEach(uc => {
      groups.add(getGroupKey(uc));
    });
    return Array.from(groups).sort();
  }, [filteredUseCases, getGroupKey]);

  // ---- axis scale ----
  const getAxisScale = () => {
    if (displayMode === 'Calibrated') {
      return { minX: 35, maxX: 80, minY: 35, maxY: 80 };
    } else if (displayMode === 'Auto') {
      const values = filteredUseCases.map(uc => uc.value_score).filter(v => v != null);
      const impls = filteredUseCases.map(uc => uc.implementation_score).filter(v => v != null);
      if (!values.length || !impls.length) return { minX: 0, maxX: 100, minY: 0, maxY: 100 };
      return {
        minX: Math.max(0, Math.min(...impls) - 5),
        maxX: Math.min(100, Math.max(...impls) + 5),
        minY: Math.max(0, Math.min(...values) - 5),
        maxY: Math.min(100, Math.max(...values) + 5)
      };
    }
    return { minX: 0, maxX: 100, minY: 0, maxY: 100 };
  };

  const axisScale = getAxisScale();
  const quadrantFilteredUseCases = filteredUseCases;
  const listUseCases = quadrantFilteredUseCases.slice(0, 15);

  // Auto-scroll selected item into view
  const selectedId = selectedUseCase?.use_case_id || highlightedUseCase?.use_case_id;
  useEffect(() => {
    if (!selectedId) return;
    const el = document.querySelector(`[data-ucid="${selectedId}"]`);
    if (el) el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selectedId, rankMode, selectedType, selectedCategory]);

  // ---- URL handlers ----
  const updateURL = useCallback((updates) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      
      Object.entries(updates).forEach(([key, value]) => {
        if (value && value !== 'all' && value !== '') {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      
      return newParams;
    });
  }, [setSearchParams]);

  const handleSearchChange = useCallback((value) => {
    updateURL({ q: value });
  }, [updateURL]);

  const handleSortChange = useCallback((value) => {
    updateURL({ sort: value });
  }, [updateURL]);

  // ---- Color assignment for points/legend ----
  const getCategoryColor = useCallback((useCase) => {
    const groupKey = getGroupKey(useCase);
    
    if (groupKey === 'Unassigned' || groupKey === 'Uncategorized') {
      return '#9ca3af';
    }
    
    const colors = ['#18365E', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
    const hash = groupKey.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  }, [getGroupKey]);

  // ---- Keyboard navigation handlers ----
  useEffect(() => {
    function handleKeyDown(e) {
      if (!listContainerRef.current || !listContainerRef.current.contains(document.activeElement)) {
        return;
      }
      
      const items = listUseCases;
      if (!items.length) return;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setKeyboardHighlightIndex(prev => {
          const next = prev < items.length - 1 ? prev + 1 : 0;
          setHighlightedUseCase(items[next]);
          const ref = listItemRefs.current[items[next]?.use_case_id];
          if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setKeyboardHighlightIndex(prev => {
          const next = prev > 0 ? prev - 1 : items.length - 1;
          setHighlightedUseCase(items[next]);
          const ref = listItemRefs.current[items[next]?.use_case_id];
          if (ref) ref.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          return next;
        });
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (keyboardHighlightIndex >= 0 && keyboardHighlightIndex < items.length) {
          const useCase = items[keyboardHighlightIndex];
          navigate(`/usecase/${useCase.use_case_id}?client=${currentClientId}&lens=${searchParams.get('lens') || '70_30'}&display=${displayMode}`);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedUseCase(null);
        setHighlightedUseCase(null);
        setKeyboardHighlightIndex(-1);
      }
    }
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [listUseCases, keyboardHighlightIndex, navigate, searchParams, displayMode, currentClientId]);

  useEffect(() => {
    setKeyboardHighlightIndex(-1);
  }, [selectedType, selectedCategory, rankMode]);

  // ---- Preview helpers ----
  const midXVal = (axisScale.minX + axisScale.maxX) / 2;
  const midYVal = (axisScale.minY + axisScale.maxY) / 2;

  const computedRank = activeUseCase
    ? (quadrantFilteredUseCases.findIndex(uc => uc.use_case_id === activeUseCase.use_case_id) + 1)
    : null;

  const quadrantLabel = (() => {
    if (!activeUseCase) return '';
    const financialValue = displayMode === 'Calibrated'
          ? (activeUseCase.value_score_cal ?? activeUseCase.value_score ?? 0)
          : (activeUseCase.bv_axis ?? activeUseCase.value_score ?? 0);
    const implementationEase = displayMode === 'Calibrated'
          ? (activeUseCase.implementation_score_cal ?? activeUseCase.implementation_score ?? 0)
          : (activeUseCase.ie_axis ?? activeUseCase.implementation_score ?? 0);

    const qx = (implementationEase >= midXVal);
    const qy = (financialValue >= midYVal);

    if (qy && qx) return 'Quick Win';
    if (qy && !qx) return 'Strategic Investment';
    if (!qy && qx) return 'Efficiency Booster';
    return 'Resource Drain';
  })();

  // Fetch use case details
  async function fetchUseCaseDetails(id) {
    if (!id) return null;
    if (!window.__useCaseCache) window.__useCaseCache = new Map();

    const cacheKey = `${id}-${currentClientId}`;
    if (window.__useCaseCache.has(cacheKey)) return window.__useCaseCache.get(cacheKey);
    
    try {
      const res = await fetch(buildApiUrl(`/api/v2/useCases/${id}?clientId=${currentClientId}`));
      if (!res.ok) throw new Error(`Details HTTP ${res.status}`);
      const json = await res.json();
      window.__useCaseCache.set(cacheKey, json);
      return json;
    } catch (err) {
      console.error('[MatrixPage] Failed to fetch use case details:', err);
      throw err;
    }
  }

  if (loading) return <div style={{ padding: 20 }}>Loading use cases for Client {currentClientId}...</div>;
  if (error)   return <div style={{ padding: 20, color: '#ef4444' }}>Error: {error}</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', backgroundColor: '#f8fafc', overflow: 'hidden' }}>
      <style>{`
        @media (prefers-reduced-motion: reduce) {
          * { animation-duration:.01ms !important; animation-iteration-count:1 !important; transition-duration:.01ms !important; }
        }
        .list-item:focus {
          outline: 2px solid #18365E;
          outline-offset: -2px;
        }
      `}</style>

      {/* Filters Toolbar - Compact, single-line layout */}
      <div ref={filterRef} style={{ borderBottom: '1px solid #f3f4f6' }}>
        <FiltersToolbar
          categoryMode={categoryMode}
          selectedCategory={selectedCategory}
          selectedType={selectedType}
          searchQuery={searchQuery}
          sortBy={sortBy}
          uniqueTypes={uniqueTypes}
          uniqueCoreCategories={uniqueCoreCategories}
          uniqueClientCategories={uniqueClientCategories}
          onCategoryModeChange={(mode) => {
            const newParams = new URLSearchParams(searchParams);
            newParams.set('mode', mode);
            newParams.delete('cat');
            newParams.delete('ccat');
            setSearchParams(newParams);
          }}
          onCategoryChange={(cat) => {
            const newParams = new URLSearchParams(searchParams);
            if (categoryMode === 'client') {
              if (cat !== 'all') newParams.set('ccat', cat);
              else newParams.delete('ccat');
            } else {
              if (cat !== 'all') newParams.set('cat', cat);
              else newParams.delete('cat');
            }
            setSearchParams(newParams);
          }}
          onTypeChange={(type) => updateURL({ type })}
          onSearchChange={handleSearchChange}
          onSortChange={handleSortChange}
        />
      </div>

      {/* Content - 3 COLUMNS */}
      <div style={{ flex: 1, display: 'flex', gap: 12, padding: '8px 12px', minHeight: 0, overflow: 'hidden' }}>
        
        {/* Left: Matrix */}
        <div style={{ flex: '1 1 auto', display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0 }}>
          <div ref={matrixCardRef} style={{
            backgroundColor: 'white', 
            borderRadius: 8, 
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            padding: 4,
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            position: 'relative',
            overflow: 'hidden'
          }}>
            <div style={{
              width: `${squareSize}px`, 
              height: `${squareSize}px`,
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center'
            }}>
              {/*
                Matrix chart delegated to MatrixSVGChart (shared component).
                MatrixPage maps its fields to the MatrixSVGChart item schema:
                  x = implementation_score (Ease of Implementation)
                  y = value_score          (Business Value)
              */}
              <MatrixSVGChart
                items={quadrantFilteredUseCases.map((uc, index) => {
                  const rank = index + 1;
                  const financialValue     = displayMode === 'Calibrated'
                    ? (uc.value_score_cal          ?? uc.value_score          ?? 0)
                    : (uc.value_score              ?? 0);
                  const implementationEase = displayMode === 'Calibrated'
                    ? (uc.implementation_score_cal  ?? uc.implementation_score ?? 0)
                    : (uc.implementation_score      ?? 0);
                  return {
                    id:    String(uc.use_case_id),
                    x:     implementationEase,
                    y:     financialValue,
                    label: uc.title || uc.name || '',
                    rank,
                  };
                })}
                axisScale={axisScale}
                selectedId={selectedUseCase ? String(selectedUseCase.use_case_id) : null}
                highlightedId={highlightedUseCase ? String(highlightedUseCase.use_case_id) : null}
                onSelect={(id) => {
                  if (id === null) {
                    setSelectedUseCase(null);
                    setHighlightedUseCase(null);
                    setKeyboardHighlightIndex(-1);
                    return;
                  }
                  const uc = quadrantFilteredUseCases.find(u => String(u.use_case_id) === id);
                  if (uc) {
                    setSelectedUseCase(uc);
                    setHighlightedUseCase(null);
                    setKeyboardHighlightIndex(-1);
                  }
                }}
                onHover={(id) => {
                  if (id === null) return;
                  const uc = quadrantFilteredUseCases.find(u => String(u.use_case_id) === id);
                  if (uc) setHighlightedUseCase(uc);
                }}
                size={squareSize}
              />
            </div>
          </div>
          
          {/* Legend */}
          {categoryMode === 'client' && legendItems.length > 0 && (
            <div style={{
              marginTop: 8,
              padding: '8px 12px',
              backgroundColor: 'white',
              borderRadius: 6,
              fontSize: 11,
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              alignItems: 'center',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <span style={{ fontWeight: 600, color: '#6b7280' }}>Client Categories:</span>
              {legendItems.slice(0, 8).map(cat => (
                <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    backgroundColor: getCategoryColor({ client_category_names: [cat] })
                  }} />
                  <span>{cat}</span>
                </div>
              ))}
              {legendItems.length > 8 && (
                <span style={{ color: '#9ca3af' }}>+{legendItems.length - 8} more</span>
              )}
            </div>
          )}
        </div>

        {/* Middle: Top 15 */}
        <div style={{
          flex: '0 0 clamp(280px, 22vw, 380px)',
          display: 'flex', flexDirection: 'column', minWidth: 280, maxWidth: 380, minHeight: 0
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            padding: 16, display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexShrink: 0 }}>
              <div>
                <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: '#18365E' }}>
                  Top 15 Priorities
                </h2>
                <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                  {window.__clientUseCaseTotal || useCases.length} total • Client {currentClientId}
                </div>
              </div>
              <span style={{ 
                fontSize: 10, 
                color: '#64748B', 
                backgroundColor: '#F1F5F9', 
                padding: '3px 8px', 
                borderRadius: 9999, 
                fontWeight: 500 
              }}>
                {rankMode === 'quickwins' ? 'Quick Wins First' : rankMode === 'strategic' ? 'Strategic Focus' : 'Balanced View'}
              </span>
            </div>

            {listUseCases.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: '#6b7280', fontSize: 13 }}>
                No use cases match your current filters.
              </div>
            ) : (
              <div 
                ref={listContainerRef}
                role="listbox"
                tabIndex={0}
                aria-label="Use cases list"
                style={{ flex: 1, overflowY: 'auto', paddingRight: 4, minHeight: 0, outline: 'none' }}
                onFocus={() => {
                  if (keyboardHighlightIndex === -1 && listUseCases.length > 0) {
                    setKeyboardHighlightIndex(0);
                    setHighlightedUseCase(listUseCases[0]);
                  }
                }}
              >
                {listUseCases.map((item, index) => {
                  if (!item) return null;
                  const rank = quadrantFilteredUseCases.findIndex(uc => uc.use_case_id === item.use_case_id) + 1;
                  const isSelected = item.use_case_id === selectedUseCase?.use_case_id;
                  const isHighlighted = item.use_case_id === highlightedUseCase?.use_case_id;
                  const isKeyboardHighlighted = index === keyboardHighlightIndex;

                  return (
                    <div 
                      key={item.use_case_id}
                      data-ucid={item.use_case_id}
                      ref={el => listItemRefs.current[item.use_case_id] = el}
                      role="option"
                      aria-selected={isSelected || isHighlighted}
                      className="list-item"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', marginBottom: 5,
                        borderRadius: 6, 
                        backgroundColor: isSelected ? '#FEF3C7' : (isHighlighted || isKeyboardHighlighted) ? '#F4F7FA' : 'white',
                        border: isSelected ? '2px solid #F59E0B' : (isHighlighted || isKeyboardHighlighted) ? '1px solid #18365E' : '1px solid #E5E7EB',
                        boxShadow: (isSelected || isHighlighted) ? 'inset 3px 0 #18365E' : 'none',
                        transition: 'all 0.2s ease', 
                        minHeight: 32,
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setSelectedUseCase(item);
                        setHighlightedUseCase(null);
                        setKeyboardHighlightIndex(index);
                      }}
                      onDoubleClick={() => {
                        navigate(`/usecase/${item.use_case_id}?client=${currentClientId}&lens=${searchParams.get('lens') || '70_30'}&display=${displayMode}`);
                      }}
                      onMouseEnter={() => {
                        setHighlightedUseCase(item);
                        setKeyboardHighlightIndex(index);
                      }}
                    >
                      <span style={{
                        minWidth: 18, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 9, fontFamily: 'monospace', fontWeight: 600,
                        color: rank <= 3 ? '#ffffff' : '#6b7280',
                        backgroundColor: rank === 1 ? '#FF6E4C' : rank <= 3 ? '#18365E' : '#F1F5F9',
                        borderRadius: 3
                      }}>{rank}</span>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 11, fontWeight: 500, color: '#111827', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.title || item.name}
                        </div>
                        {categoryMode === 'client' && item.client_category_names?.length > 0 && (
                          <div style={{ fontSize: 9, color: '#6b7280', marginTop: 1 }}>
                            {item.client_category_names.join(', ')}
                          </div>
                        )}
                      </div>

                      {isInProspectList(item.use_case_id) ? (
                        <button
                          disabled
                          style={{
                            fontSize: 10,
                            padding: '3px 7px',
                            backgroundColor: '#f3f4f6',
                            color: '#6b7280',
                            border: '1px solid #e5e7eb',
                            borderRadius: 3,
                            cursor: 'not-allowed',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 3
                          }}
                          title="Already in prospect list"
                        >
                          <span style={{ fontSize: 12 }}>✓</span> Added
                        </button>
                      ) : (
                        <button
                          onClick={async (e) => {
                            e.stopPropagation();
                            if (onUseCaseClick) {
                              await onUseCaseClick(item.use_case_id);
                              await refetchStatuses();
                            }
                          }}
                          style={{
                            fontSize: 10,
                            padding: '3px 7px',
                            backgroundColor: '#FF6E4C',
                            color: 'white',
                            border: 'none',
                            borderRadius: 3,
                            cursor: 'pointer',
                            fontWeight: 500,
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={(e) => { e.target.style.backgroundColor = '#ff5533'; }}
                          onMouseLeave={(e) => { e.target.style.backgroundColor = '#FF6E4C'; }}
                          title="Add to prospect list"
                        >
                          Add
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {(filteredUseCases.length > 15) && (
              <div style={{ textAlign: 'center', paddingTop: 6, borderTop: '1px solid #f3f4f6', marginTop: 'auto', flexShrink: 0 }}>
                <button 
                  onClick={() => navigate('/results?' + searchParams.toString())}
                  style={{
                  fontSize: 10, color: '#6b7280', backgroundColor: 'transparent', border: 'none', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 3, margin: '0 auto', padding: '3px 6px', borderRadius: 4, transition: 'color 0.2s ease'
                }}
                  onMouseEnter={(e) => { e.currentTarget.style.color = '#374151'; e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.color = '#6b7280'; e.currentTarget.style.backgroundColor = 'transparent'; }}
                >
                  Show All ({filteredUseCases.length})
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M7 17L17 7M17 7H7M17 7V17"></path>
                  </svg>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right: Preview Panel */}
        <div style={{
          flex: '0 0 clamp(280px, 24vw, 420px)',
          display: 'flex', flexDirection: 'column', minWidth: 280, maxWidth: 420, minHeight: 0
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: 8,
            boxShadow: '0 1px 2px rgba(15, 23, 42, 0.06)',
            border: '1px solid #E2E8F0',
            padding: 14,
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0
          }}>
            <UseCasePreview
              useCase={activeUseCase}
              cal={activeUseCase ? calMap[activeUseCase.use_case_id] : null}
              displayMode={displayMode}
              rankLabel={rankMode}
              rank={computedRank}
              quadrantLabel={quadrantLabel}
              onClear={() => { 
                setSelectedUseCase(null); 
                setHighlightedUseCase(null);
                setKeyboardHighlightIndex(-1);
              }}
              onStep={(dir) => {
                if (!activeUseCase) return;
                const idx = quadrantFilteredUseCases.findIndex(uc => uc.use_case_id === activeUseCase.use_case_id);
                if (idx === -1) return;
                const next = quadrantFilteredUseCases[(idx + dir + quadrantFilteredUseCases.length) % quadrantFilteredUseCases.length];
                setHighlightedUseCase(next);
                const listIdx = listUseCases.findIndex(uc => uc.use_case_id === next.use_case_id);
                if (listIdx !== -1) setKeyboardHighlightIndex(listIdx);
              }}
              fetchDetails={fetchUseCaseDetails}
              onViewProfile={(useCase) => {
                navigate(`/usecase/${useCase.use_case_id}?client=${currentClientId}&lens=${searchParams.get('lens') || '70_30'}&display=${displayMode}`);
              }}
              onAddToProspect={async (useCaseId) => {
                if (onUseCaseClick) {
                  await onUseCaseClick(useCaseId);
                  await refetchStatuses();
                }
              }}
              isInProspectList={activeUseCase ? isInProspectList(activeUseCase.use_case_id) : false}
            />
          </div>
        </div>
      </div>
    </div>
  );
}